/**
 * UndoManager - Manages undo/redo stacks for editor operations.
 * Uses the command pattern: each action has undo() and redo() functions.
 */
export default class UndoManager {
  constructor(options = {}) {
    this._undoStack = [];
    this._redoStack = [];
    this._maxSize = options.maxSize || 50;
    this._onChange = null;
  }

  /**
   * Push a new undoable action. Clears the redo stack.
   * @param {{ undo: Function, redo: Function, description: string }} action
   */
  push(action) {
    this._undoStack.push(action);
    if (this._undoStack.length > this._maxSize) {
      this._undoStack.shift();
    }
    this._redoStack.length = 0;
    this._notify();
  }

  /**
   * Undo the most recent action.
   * @returns {boolean} true if an action was undone
   */
  undo() {
    if (this._undoStack.length === 0) return false;
    const action = this._undoStack.pop();
    action.undo();
    this._redoStack.push(action);
    this._notify();
    return true;
  }

  /**
   * Redo the most recently undone action.
   * @returns {boolean} true if an action was redone
   */
  redo() {
    if (this._redoStack.length === 0) return false;
    const action = this._redoStack.pop();
    action.redo();
    this._undoStack.push(action);
    this._notify();
    return true;
  }

  canUndo() { return this._undoStack.length > 0; }
  canRedo() { return this._redoStack.length > 0; }

  /**
   * Clear all undo/redo history.
   */
  clear() {
    this._undoStack.length = 0;
    this._redoStack.length = 0;
    this._notify();
  }

  /**
   * Set the change callback (for UI button enable/disable).
   * @param {Function} fn - Called with this UndoManager instance
   */
  setOnChange(fn) {
    this._onChange = fn;
  }

  /** @private */
  _notify() {
    if (this._onChange) this._onChange(this);
  }
}
